// The content bundle kept in the browser by its hash (IndexedDB, one entry): IDENTIFY names the kept hash as the live
// client names the bundle it holds, so a READY without a content change carries no bundle and the store loads the kept
// text instead. A failure anywhere (no IndexedDB, a private window, quota) reads as nothing kept.

const DB = 'gc-beta';
const STORE = 'content';

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(STORE);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => resolve(null);
      r.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function get<T>(db: IDBDatabase, key: string): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      const r = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      r.onsuccess = () => resolve((r.result as T | undefined) ?? null);
      r.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

let dbLoad: Promise<IDBDatabase | null> | null = null;
const db = (): Promise<IDBDatabase | null> => (dbLoad ??= open());

/** The kept bundle's hash (a small read: IDENTIFY waits on it), or null. */
export async function keptHash(): Promise<string | null> {
  const d = await db();
  return d ? get<string>(d, 'hash') : null;
}

/** The kept bundle's text when it is the one with this hash, else null. */
export async function keptBundle(hash: string): Promise<string | null> {
  const d = await db();
  if (!d) return null;
  const [h, text] = await Promise.all([get<string>(d, 'hash'), get<string>(d, 'bundle')]);
  return h === hash && typeof text === 'string' ? text : null;
}

/** Keep a bundle's text under its hash (the one entry replaced). */
export async function keepBundle(hash: string, text: string): Promise<void> {
  const d = await db();
  if (!d) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = d.transaction(STORE, 'readwrite');
      const st = tx.objectStore(STORE);
      // the hash goes in after the text: a write cut short leaves no hash naming a text that is not there
      st.delete('hash');
      st.put(text, 'bundle');
      st.put(hash, 'hash');
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}
